import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePlayer } from '../contexts/PlayerContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ArrowLeft, Users, Target, CheckCircle, XCircle, Trophy } from 'lucide-react';
import io, { Socket } from 'socket.io-client';

interface GuessQuestion {
  id: string;
  question: string;
  correctAnswers: string[];
  caseSensitive: boolean;
  order: number;
}

interface GuessTheValueGame {
  id: string;
  title: string;
  description: string;
  correctAnswer?: string; // Backward compatibility
  caseSensitive?: boolean; // Backward compatibility
  questions?: GuessQuestion[]; // New: array of questions
  questionCount?: number; // New: track number of questions
  mode: 'multiple' | 'collect';
  state: 'open' | 'active' | 'closed';
}

function GuessGameApp() {
  const { gameId } = useParams<{ gameId: string }>();
  const { player, isAuthenticated } = usePlayer();
  const navigate = useNavigate();

  const [game, setGame] = useState<GuessTheValueGame | null>(null);
  const [currentGuess, setCurrentGuess] = useState('');
  const [guesses, setGuesses] = useState<Array<{ guess: string; isCorrect: boolean }>>([]);
  const [hasCorrectGuess, setHasCorrectGuess] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastGuessCorrect, setLastGuessCorrect] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [socketConnected, setSocketConnected] = useState(false);
  
  // New: track current question and progress
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState<boolean[]>([]);
  const [allQuestionsComplete, setAllQuestionsComplete] = useState(false);
  
  const socketRef = useRef<Socket | null>(null);
  const hasJoinedRoom = useRef(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Initialize socket connection
  useEffect(() => {
    const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
    console.log('🔌 Connecting to guess game server:', serverUrl);
    
    const socket = io(serverUrl, {
      path: '/ws/',
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('🔌 Connected to guess game server');
      setSocketConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('🔌 Disconnected from guess game server');
      setSocketConnected(false);
      hasJoinedRoom.current = false;
    });

    socket.on('guess_game_started', (data: { guessGame: GuessTheValueGame }) => {
      console.log('🎯 Guess game started/updated event received:', data.guessGame);
      setGame(data.guessGame);
    });

    socket.on('guess_game_closed', (data: { guessGame: GuessTheValueGame }) => {
      console.log('🎯 Guess game closed event received:', data.guessGame);
      setGame(data.guessGame);
      // Navigate to leaderboard when game closes
      if (gameId) {
        navigate(`/guess-leaderboard/${gameId}`);
      }
    });

    socket.on('guess_submitted', (data: { playerId: string; isCorrect: boolean; totalGuesses: number }) => {
      console.log('🎯 Someone submitted a guess:', data);
    });

    return () => {
      console.log('🔌 Cleaning up socket connection');
      socket.disconnect();
    };
  }, []);

  // Join the guess game room
  useEffect(() => {
    if (gameId && socketConnected && !hasJoinedRoom.current && socketRef.current) {
      console.log('🚪 Joining guess game room:', gameId);
      socketRef.current.emit('join', { room: gameId });
      hasJoinedRoom.current = true;
    }
  }, [gameId, socketConnected]);

  // Fetch game data
  useEffect(() => {
    const fetchGame = async () => {
      if (!gameId) return;

      try {
        const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
        const response = await fetch(`${serverUrl}/api/admin/guess-games/${gameId}`);
        
        if (response.ok) {
          const data = await response.json();
          setGame(data.guessGame);
        } else {
          console.error('Failed to fetch guess game');
        }
      } catch (error) {
        console.error('Error fetching guess game:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGame();
  }, [gameId]);

  // Auto-navigate to leaderboard when all questions are complete
  useEffect(() => {
    if (allQuestionsComplete && gameId) {
      console.log('🏆 All questions complete, navigating to leaderboard...');
      const timer = setTimeout(() => {
        navigate(`/guess-leaderboard/${gameId}`);
      }, 2500); // Give user time to see completion message
      return () => clearTimeout(timer);
    }
  }, [allQuestionsComplete, gameId, navigate]);

  const checkAnswer = (guess: string): boolean => {
    if (!game) return false;

    // New structure: use questions array
    if (game.questions && game.questions.length > 0) {
      const currentQuestion = game.questions[currentQuestionIndex];
      if (!currentQuestion) return false;

      const caseSensitive = currentQuestion.caseSensitive;
      const userGuess = caseSensitive ? guess : guess.toLowerCase();
      
      // Check if guess matches any of the correct answers
      return currentQuestion.correctAnswers.some(answer => {
        const correctAnswer = caseSensitive ? answer : answer.toLowerCase();
        return userGuess.trim() === correctAnswer.trim();
      });
    }
    
    // Backward compatibility: old single answer structure
    if (game.correctAnswer) {
      const caseSensitive = game.caseSensitive || false;
      const userGuess = caseSensitive ? guess : guess.toLowerCase();
      const correctAnswer = caseSensitive ? game.correctAnswer : game.correctAnswer.toLowerCase();
      return userGuess.trim() === correctAnswer.trim();
    }

    return false;
  };

  const handleSubmitGuess = async () => {
    if (!game || !currentGuess.trim() || !player) return;

    const isCorrect = checkAnswer(currentGuess);
    const newGuess = { guess: currentGuess, isCorrect };

    if (game.mode === 'multiple') {
      // Multiple guess mode: show feedback and allow retry
      setGuesses([...guesses, newGuess]);
      setLastGuessCorrect(isCorrect);
      setShowFeedback(true);

      if (isCorrect) {
        setHasCorrectGuess(true);
        
        // Mark this question as answered
        const newQuestionsAnswered = [...questionsAnswered];
        newQuestionsAnswered[currentQuestionIndex] = true;
        setQuestionsAnswered(newQuestionsAnswered);
        
        // Check if there are more questions
        const totalQuestions = game.questions?.length || 1;
        const nextQuestionIndex = currentQuestionIndex + 1;
        
        if (nextQuestionIndex < totalQuestions) {
          // Move to next question after a short delay
          setTimeout(() => {
            setCurrentQuestionIndex(nextQuestionIndex);
            setCurrentGuess('');
            setGuesses([]);
            setHasCorrectGuess(false);
            setShowFeedback(false);
          }, 2000);
        } else {
          // All questions complete!
          setAllQuestionsComplete(true);
        }
      } else {
        // Clear input if incorrect to allow retry
        setTimeout(() => {
          setCurrentGuess('');
          setShowFeedback(false);
        }, 2000);
      }
    } else {
      // Collect answer mode: accept first submission only
      setGuesses([newGuess]);
      setHasSubmitted(true);
      setHasCorrectGuess(isCorrect);
      setLastGuessCorrect(isCorrect);
      setShowFeedback(true);
      
      // Mark this question as answered
      const newQuestionsAnswered = [...questionsAnswered];
      newQuestionsAnswered[currentQuestionIndex] = true;
      setQuestionsAnswered(newQuestionsAnswered);
      
      // In collect mode, move to next question immediately
      const totalQuestions = game.questions?.length || 1;
      const nextQuestionIndex = currentQuestionIndex + 1;
      
      if (nextQuestionIndex < totalQuestions) {
        setTimeout(() => {
          setCurrentQuestionIndex(nextQuestionIndex);
          setCurrentGuess('');
          setGuesses([]);
          setHasSubmitted(false);
          setHasCorrectGuess(false);
          setShowFeedback(false);
        }, 2000);
      } else {
        // All questions complete!
        setAllQuestionsComplete(true);
      }
    }

    // Submit to server with questionIndex
    try {
      const serverUrl = import.meta.env.VITE_SERVER_URL || 'http://localhost:3001';
      await fetch(`${serverUrl}/api/guess-games/${gameId}/guess`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId: player.id,
          playerNickname: player.nickname,
          guess: currentGuess,
          questionIndex: currentQuestionIndex,
        }),
      });
    } catch (error) {
      console.error('Error submitting guess:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitDisabled()) {
      handleSubmitGuess();
    }
  };

  const isSubmitDisabled = () => {
    if (!game || !currentGuess.trim()) return true;
    if (hasCorrectGuess) return true;
    if (game.mode === 'collect' && hasSubmitted) return true;
    return false;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading game...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="font-sans font-semibold text-lg">Game Not Found</h1>
          </div>
          <Card>
            <CardContent className="p-6 text-center">
              <p className="font-mono text-muted-foreground">This game doesn't exist or has been removed.</p>
              <Button onClick={() => navigate('/lobby')} className="mt-4">
                Back to Lobby
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/lobby')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              <span>Playing</span>
            </div>
            {game.state === 'active' && (
              <Badge variant="secondary" className="bg-green-100 text-green-800">
                Active
              </Badge>
            )}
            {game.state === 'open' && (
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                Open
              </Badge>
            )}
            {game.state === 'closed' && (
              <Badge variant="outline" className="text-xs">
                CLOSED
              </Badge>
            )}
          </div>
        </div>

        {/* Game Information */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-primary" />
              <Badge variant="outline" className="text-xs">
                {game.mode === 'multiple' ? 'Multiple Attempts' : 'One Attempt Only'}
              </Badge>
              {/* Show question progress if multiple questions */}
              {game.questions && game.questions.length > 1 && (
                <Badge variant="secondary" className="text-xs">
                  Question {currentQuestionIndex + 1} of {game.questions.length}
                </Badge>
              )}
            </div>
            <CardTitle className="font-sans font-semibold text-xl leading-tight">{game.title}</CardTitle>
            <CardDescription className="font-mono">{game.description}</CardDescription>
            
            {/* Show current question text if using new structure */}
            {game.questions && game.questions.length > 0 && game.questions[currentQuestionIndex] && (
              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="font-semibold text-lg">{game.questions[currentQuestionIndex].question}</p>
                {game.questions[currentQuestionIndex].caseSensitive && (
                  <Badge variant="outline" className="text-xs mt-2">
                    Case Sensitive
                  </Badge>
                )}
              </div>
            )}
          </CardHeader>
        </Card>

        {/* All Questions Complete State */}
        {allQuestionsComplete && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <div className="flex justify-center mb-3">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                  <Trophy className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <h3 className="font-sans font-semibold text-lg text-green-800 mb-2">All Questions Complete!</h3>
              <p className="font-mono text-sm text-green-700">
                You've finished all {game.questions?.length || 0} questions.
              </p>
              <p className="font-mono text-xs text-green-600 mt-2">
                Redirecting to leaderboard...
              </p>
              <Button 
                onClick={() => navigate(`/guess-leaderboard/${gameId}`)}
                className="mt-4 w-full"
                size="lg"
              >
                View Leaderboard Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Success State for Current Question */}
        {hasCorrectGuess && !allQuestionsComplete && (
          <Card className="mb-6 bg-green-50 border-green-200">
            <CardContent className="p-6 text-center">
              <div className="flex justify-center mb-3">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <h3 className="font-sans font-semibold text-lg text-green-800 mb-2">Correct!</h3>
              <p className="font-mono text-sm text-green-700">
                Moving to the next question...
              </p>
              {game.mode === 'multiple' && guesses.length > 1 && (
                <p className="font-mono text-xs text-green-600 mt-2">
                  It took you {guesses.length} {guesses.length === 1 ? 'attempt' : 'attempts'}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Collect Mode - Submitted State */}
        {game.mode === 'collect' && hasSubmitted && !hasCorrectGuess && (
          <Card className="mb-6 bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center">
              <div className="flex justify-center mb-3">
                <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-blue-600" />
                </div>
              </div>
              <h3 className="font-sans font-semibold text-lg text-blue-800 mb-2">Answer Submitted!</h3>
              <p className="font-mono text-sm text-blue-700">
                Your guess: <span className="font-semibold">{guesses[0]?.guess}</span>
              </p>
              <p className="font-mono text-xs text-blue-600 mt-2">
                The admin will review all submissions at the end.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Input Section */}
        {!hasCorrectGuess && !(game.mode === 'collect' && hasSubmitted) && game.state !== 'closed' && !allQuestionsComplete && (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="guess-input" className="font-mono text-sm font-medium mb-2 block">
                    Your Guess
                  </label>
                  <Input
                    id="guess-input"
                    type="text"
                    value={currentGuess}
                    onChange={(e) => setCurrentGuess(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type your answer here..."
                    className="font-mono"
                    disabled={game.state !== 'active'}
                  />
                  {/* Show case sensitive warning for current question */}
                  {game.questions && game.questions[currentQuestionIndex]?.caseSensitive && (
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      ⚠️ Remember: Answer is case sensitive
                    </p>
                  )}
                  {/* Backward compatibility for old structure */}
                  {!game.questions && game.caseSensitive && (
                    <p className="text-xs text-muted-foreground font-mono mt-1">
                      ⚠️ Remember: Answer is case sensitive
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleSubmitGuess}
                  disabled={isSubmitDisabled()}
                  className="w-full"
                  size="lg"
                >
                  Submit Guess
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback for incorrect guess (multiple mode only) */}
        {showFeedback && !lastGuessCorrect && game.mode === 'multiple' && !hasCorrectGuess && (
          <Card className="mb-6 bg-red-50 border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-800">
                <XCircle className="h-5 w-5" />
                <span className="font-mono text-sm font-medium">
                  Not quite right. Try again!
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Previous Attempts (multiple mode only) */}
        {game.mode === 'multiple' && guesses.length > 0 && !hasCorrectGuess && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="font-sans font-semibold text-sm">
                Previous Attempts ({guesses.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {guesses.slice().reverse().map((guess, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="font-mono text-sm">{guess.guess}</span>
                    <XCircle className="h-4 w-4 text-red-500" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button variant="ghost" className="w-full" onClick={() => navigate('/lobby')}>
            Back to Lobby
          </Button>
        </div>
      </div>
    </div>
  );
}

export default GuessGameApp;


